// @ts-strict-ignore
import React, { useState } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Checkbox,
  Input,
  InputChipList,
  InputChipListChip,
  InputChipListProps,
} from '@node-space/ui-components'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { ApiKeyRequestForm } from 'types/apiKeys'
import { apiKeySchema } from './schema'
import { isIPRangeValid, isIPValid } from './validation'

type ApiKeyFormData = {
  description: string
  withdrawals: boolean
  payroll: boolean
  readOnly: boolean
  allowedIps: InputChipListChip[]
}

export type ApiKeyCreateForm = {
  payload?: ApiKeyRequestForm
  onSubmit: SubmitHandler<ApiKeyFormData>
  isLoading?: boolean
  requestError?: string
  isEditMode?: boolean
  dismissAction?: () => void
  enablePayrollRole?: boolean
}

const ApiKeyCreateForm = ({
  payload,
  onSubmit,
  isLoading = false,
  isEditMode = false,
  requestError,
  dismissAction,
  enablePayrollRole,
}: ApiKeyCreateForm) => {
  const { t } = useTranslation()
  const [isAllowedIpsError, setIsAllowedIpsError] = useState<string | null>(null)
  const payloadReadonly = payload?.readOnly ?? false
  const isReadonlyEditMode = isEditMode && payloadReadonly

  const {
    control,
    register,
    formState: { errors, isValid },
    handleSubmit,
    setValue,
    clearErrors,
  } = useForm<ApiKeyFormData>({
    defaultValues: {
      allowedIps: payload?.allowedIps ?? [],
      description: payload?.description ?? '',
      withdrawals: payload?.withdrawals ?? true,
      payroll: enablePayrollRole,
      readOnly: payloadReadonly,
    },
    resolver: yupFormResolver<ApiKeyFormData>(apiKeySchema()),
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const [allowedIps, withdrawals, readOnly] = useWatch({
    control,
    name: ['allowedIps', 'withdrawals', 'readOnly'],
  })

  const isFormValid = (isValid && !!allowedIps.length) || (isValid && !withdrawals)

  const handleAllowedIpsChange = (values: InputChipListProps['chips']) => {
    const lastAddedIp = values[values.length - 1]

    if (lastAddedIp) {
      const isCIDR = lastAddedIp.label.includes('/')

      if (isCIDR && !isIPRangeValid(lastAddedIp.label)) {
        setIsAllowedIpsError(t('manageApiKeys.invalidIPRange'))
        return
      }

      if (!isCIDR && !isIPValid(lastAddedIp.label)) {
        setIsAllowedIpsError(t('manageApiKeys.invalidIPEntry'))
        return
      }
    }

    clearErrors('allowedIps')
    setIsAllowedIpsError(null)
    setValue('allowedIps', values)
  }

  return (
    <Box tag="form" flexCol gapY={16} data-testid="apikey-create" onSubmit={handleSubmit(onSubmit)}>
      <Box paddingT={24} gapY={16} flexCol>
        <Input
          {...register('description')}
          onChange={e => setValue('description', e.target.value, { shouldValidate: true })}
          label={t('name')}
          placeholder={t('manageApiKeys.keyDescription')}
          data-testid="api-key-name"
          error={Boolean(errors?.description)}
          errorText={errors?.description?.message}
          disabled={isLoading}
        />

        <InputChipList
          label={t('manageApiKeys.allowedIpAddressInputLabel')}
          placeholderText={t('manageApiKeys.allowedIpAddressInputPlaceholder')}
          optionalText={!withdrawals && t('optionalLabel')}
          name="allowedIps"
          data-testid="api-key-allowed-ips"
          onChange={handleAllowedIpsChange}
          chips={allowedIps}
          error={Boolean(isAllowedIpsError) || Boolean(errors?.allowedIps)}
          errorText={isAllowedIpsError || errors?.allowedIps?.message}
          disabled={isLoading}
          inputMinWidth="400px"
        />

        <Box flexCol gapY={16} width="full">
          <p className="text-label text-foreground-prominent">
            {t('manageApiKeys.enableAdditionally')}
          </p>

          <Checkbox
            id="withdrawals"
            name="withdrawals"
            testId="api-key-withdrawals"
            checked={Boolean(withdrawals)}
            label={t('manageApiKeys.enableWithdrawalsLabel')}
            disabled={isReadonlyEditMode}
            onCheckedChange={value => {
              setValue('withdrawals', Boolean(value))
              if (value) {
                setValue('readOnly', false)
              }
            }}
          />

          <Checkbox
            id="readOnly"
            name="readOnly"
            testId="api-key-read-only"
            checked={Boolean(readOnly)}
            disabled={isEditMode}
            onCheckedChange={value => {
              setValue('readOnly', Boolean(value))
              if (value) {
                setValue('withdrawals', false)
              }
            }}
            label={t('manageApiKeys.readOnlyLabel')}
          />
        </Box>
        {!requestError && (
          <Box className="text-body-regular text-foreground-action-destructive">{requestError}</Box>
        )}
      </Box>

      <Box flex justify="between" paddingT={8}>
        <Button variant="secondary" onClick={dismissAction}>
          {t('cancel')}
        </Button>

        <Button
          data-testid={t('continue')}
          variant="primary"
          loading={isLoading}
          disabled={!isFormValid}
          type="submit"
        >
          {isEditMode ? t('save') : t('continue')}
        </Button>
      </Box>
    </Box>
  )
}

export default React.memo(ApiKeyCreateForm)
